import { Typography } from '@mui/material'
import { UseFormReturn } from 'react-hook-form'
import { Resource } from 'utoniq-ui'

import { ArtistCreateEventInput } from '@/clients/utoniq-core/schema'
import { GPSMapModalProps } from '@/components/pages/ConsoleMissionCreate/template/GPSMapModal'
import { tLabel, tMessage, UseFormProps, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

import {
  EventInputDateCell,
  EventInputLocationCell,
  EventPublishDateCell,
  TextInputCell,
  ThumbnailUploadCell,
  ToggleInputCell,
} from './components'

export type EventCreateFormProps = {
  artistId: string
  method: UseFormReturn<CreateEventValue>
  fileUploadInfo: FileUploadInfo
  gpsClientInfo: {
    permission: boolean
    currentLocation?: GPSMapModalProps['currentData']
  }
  onPlaceSearch: GPSMapModalProps['onSearch']
  predictions: GPSMapModalProps['predictions']
  place: GPSMapModalProps['place']
  onSelectPlace: GPSMapModalProps['onSelectPlace']
  onRequestGPSPermission: () => void
}

export type CreateEventValue = Omit<ArtistCreateEventInput, 'closeDate'> & {
  thumbnailResource: Resource | null
  isPublished: boolean
  closeDate: { date: ArtistCreateEventInput['startedAt'] }[]
}

export type FileUploadInfo = {
  onDrop: () => Promise<Resource | undefined>
  progress: number
}

export const schema: UseFormProps['schema'] = o => ({
  title: o.string().max(100).label(tLabel('consoleEventCreate.title')),
  description: o
    .string()
    .max(1000)
    .label(tLabel('consoleEventCreate.description')),
  startedAt: o
    .date()
    .allow(null, '')
    .optional()
    .label(tLabel('consoleEventCreate.startedAt')),
  endedAt: o
    .date()
    .allow(null, '')
    .optional()
    .label(tLabel('consoleEventCreate.endedAt')),
  closeDate: o
    .array()
    .items({
      //id of field array
      _id: o.string().optional(),
      date: o
        .date()
        .allow(null, '')
        .optional()
        .label(tLabel('consoleEventCreate.closeDate')),
    })
    .allow(null)
    .optional(),
  location: o
    .object({
      latitude: o
        .number()
        .label(tLabel('consoleEventCreate.location.latitude')),
      longitude: o
        .number()
        .label(tLabel('consoleEventCreate.location.longitude')),
      approximateAddress: o.string().when('latitude', {
        is: 0,
        then: o.string().when('mapUri', {
          is: o.string().exist(),
          then: o
            .string()
            .max(50)
            .label(tLabel('consoleEventCreate.location.approximateAddress')),
          otherwise: o
            .string()
            .max(50)
            .optional()
            .allow(null, '')
            .label(tLabel('consoleEventCreate.location.approximateAddress')),
        }),
        otherwise: o
          .string()
          .max(50)
          .label(tLabel('consoleEventCreate.location.approximateAddress')),
      }),
      mapUri: o.string().when('latitude', {
        is: 0,
        then: o
          .string()
          .uri()
          .optional()
          .allow(null, '')
          .label(tLabel('consoleEventCreate.location.mapUri')),
        otherwise: o
          .string()
          .uri()
          .label(tLabel('consoleEventCreate.location.mapUri')),
      }),
    })
    .allow(null)
    .optional(),
  thumbnailPath: o.string().label(tLabel('consoleEventCreate.thumbnailPath')),
  note: o
    .string()
    .max(1000)
    .optional()
    .allow(null, '')
    .label(tLabel('consoleEventCreate.note')),
  thumbnailResource: o.object().allow(null).optional(),
  isPublished: o.boolean(),
  publishStartedAt: o
    .date()
    .allow(null, '')
    .optional()
    .label(tLabel('consoleEventCreate.publishStartedAt')),
  publishEndedAt: o
    .date()
    .allow(null, '')
    .optional()
    .custom((value: Date, helpers) => {
      const [data] = helpers?.state?.ancestors || []
      if (!value) {
        return value
      }
      const publishStartedAt = data?.publishStartedAt || new Date()
      if (publishStartedAt > value) {
        // 開始日が終了日を過ぎていたらエラー
        return helpers.message({
          custom: tMessage('consoleEventCreate.publishEndedAt.custom.over'),
        })
      }
      return value
    })
    .label(tLabel('consoleEventCreate.publishEndedAt')),
})

export const EventCreateForm: Organism<EventCreateFormProps> = ({
  artistId,
  method,
  fileUploadInfo,
  ...gpsModalProps
}) => {
  const { t } = useTranslation('consoleEventCreate')
  const {
    watch,
    setValue,
    control,
    register,
    formState: { errors },
    trigger,
  } = method

  return (
    <OrganismFrame sx={{ pt: 5 }}>
      <ThumbnailUploadCell
        watch={watch}
        setValue={setValue}
        error={errors?.thumbnailPath}
        fileUploadInfo={fileUploadInfo}
      />
      <TextInputCell
        type="text"
        {...register('title')}
        error={errors?.title}
        displayRequire={{ display: true }}
        displayCount={{
          display: true,
          count: watch('title')?.length,
          limit: 100,
        }}
      />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        {...register('description')}
        error={errors?.description}
        displayRequire={{ display: true }}
        displayCount={{
          display: true,
          count: watch('description')?.length,
          limit: 1000,
        }}
      />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        {...register('note')}
        error={errors?.note}
        displayCount={{
          display: true,
          count: watch('note')?.length,
          limit: 1000,
        }}
      />
      <EventInputDateCell
        errors={errors}
        control={control}
        setValue={setValue}
        watch={watch}
        startedAt={watch('startedAt')}
        endedAt={watch('endedAt')}
      />
      <EventInputLocationCell
        artistId={artistId}
        location={watch('location')}
        setValue={setValue}
        inputApproximateAddress={{
          ...register('location.approximateAddress'),
          error: errors?.location?.approximateAddress,
        }}
        inputMapUri={{
          ...register('location.mapUri'),
          error: errors?.location?.mapUri,
        }}
        {...gpsModalProps}
      />
      <ToggleInputCell
        content={{
          title: t('isPublishedTitle'),
          description: (
            <Typography variant="caption" color="text.secondary">
              {t('isPublishedDesc')}
            </Typography>
          ),
        }}
        {...register('isPublished')}
        checked={watch('isPublished')}
      />
      <EventPublishDateCell
        watch={watch}
        setValue={setValue}
        errors={[
          errors.publishStartedAt,
          errors.publishEndedAt,
          errors.isPublished,
        ]}
        trigger={trigger}
      />
    </OrganismFrame>
  )
}

export const defaultValues: CreateEventValue = {
  title: '',
  description: '',
  thumbnailPath: '',
  thumbnailResource: null,
  note: '',
  startedAt: null,
  endedAt: null,
  location: {
    latitude: 0,
    longitude: 0,
    approximateAddress: '',
    mapUri: '',
  },
  closeDate: [{ date: null }],
  isPublished: false,
  publishStartedAt: null,
  publishEndedAt: null,
}
